import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  increment,
  deleteField,
  query,
  where,
  writeBatch,
  getCountFromServer
} from 'firebase/firestore';
import { db } from '../firebase';
import { Workspace, CreateWorkspaceInput, UpdateWorkspaceInput, WorkspaceStats } from '../types/workspaces';
import { User } from '../types/users';
import { isDemoWorkspace } from '@/utils/apiHelpers';

// Block type is used in getMockWorkspaceStats function

export const getWorkspaces = async (): Promise<{ workspaces: Workspace[] }> => {
  const workspacesRef = collection(db, 'workspaces');
  const snapshot = await getDocs(workspacesRef);
  const workspaces = snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  } as Workspace));
  return { workspaces };
};

export const getWorkspaceById = async (id: string): Promise<Workspace | null> => {
  const workspaceRef = doc(db, 'workspaces', id);
  const docSnap = await getDoc(workspaceRef);
  
  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data()
  } as Workspace;
};

export const createWorkspace = async (workspaceData: CreateWorkspaceInput): Promise<Workspace> => {
  const workspacesRef = collection(db, 'workspaces');

  
      const workspaceToCreate = {
      ...workspaceData,
      onboardingStep: workspaceData.onboardingStep ?? 0,
    };

  const docRef = await addDoc(workspacesRef, workspaceToCreate);
  
      return {
      id: docRef.id,
      ...workspaceData,
      status: 'active' as const
    };
};

export const updateWorkspace = async (id: string, workspaceData: UpdateWorkspaceInput): Promise<void> => {
  const workspaceRef = doc(db, 'workspaces', id);
  await updateDoc(workspaceRef, {
    ...workspaceData,
    updatedAt: new Date()
  });
};

export const deleteWorkspace = async (id: string): Promise<void> => {
  const workspaceRef = doc(db, 'workspaces', id);
  await deleteDoc(workspaceRef);
};

// Mock workspace stats for demo mode - will be populated dynamically
export const mockWorkspaceStats: WorkspaceStats = {
  blocks: {},
  totalAssets: 235,
  totalEmployees: 50,
  totalVendors: 50,
  totalResidents: 150, // Updated to match the actual mock data
  totalUsers: 25,
  lastUpdated: Date.now()
};

// Function to get language-aware mock workspace stats
export async function getMockWorkspaceStats(language: string = 'en'): Promise<WorkspaceStats> {
  try {
    // Import mock data to get actual counts
    const { loadMockData } = await import('@/mock');
    const blocks = await loadMockData<any[]>('blocks', language);
    const employees = await loadMockData<any[]>('employees', language);
    const vendors = await loadMockData<any[]>('vendors', language);
    const assets = await loadMockData<any[]>('assets', language);
    const residents = await loadMockData<any[]>('residents', language);
    
    // Create block stats from the loaded blocks
    const blockStats: Record<string, any> = {};
    
    if (blocks && Array.isArray(blocks)) {
      blocks.forEach(block => {
        blockStats[block.id] = {
          totalApartmentUnits: 0, // Will be calculated by recalculateWorkspaceStats
          totalParkings: 0,
          totalAmenities: 0,
          totalFloors: block.floors ? block.floors.length : 0
        };
      });
    }
    
    const result = {
      blocks: blockStats,
      totalAssets: assets?.length || 235,
      totalEmployees: employees?.length || 50,
      totalVendors: vendors?.length || 50,
      totalResidents: residents?.length || 30, // Use actual mock data count
      totalUsers: 25, // Keep this as is since it's not in the resources
      lastUpdated: Date.now()
    };
    
    return result;
  } catch (error) {
    console.warn('Error loading mock workspace stats, using fallback:', error);
    return mockWorkspaceStats;
  }
}

export async function getWorkspaceStats(firestoreUser: User): Promise<WorkspaceStats> {
  // Demo mode check
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 300));
    return await getMockWorkspaceStats(firestoreUser.language);
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const workspaceRef = doc(db, 'workspaces', firestoreUser.workspace.id);
    const workspaceDoc = await getDoc(workspaceRef);
    
    if (!workspaceDoc.exists()) {
      throw new Error('Workspace not found');
    }

    const workspaceData = workspaceDoc.data();
    return workspaceData.stats || {
      blocks: {},
      totalAssets: 0,
      totalEmployees: 0,
      totalVendors: 0,
      totalResidents: 0,
      totalUsers: 0,
      lastUpdated: Date.now()
    };
  } catch (error) {
    console.error('Error fetching workspace stats:', error);
    throw new Error('Failed to fetch workspace stats');
  }
}

export async function updateWorkspaceStats(
  firestoreUser: User, 
  updates: Partial<WorkspaceStats>
): Promise<void> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const workspaceRef = doc(db, 'workspaces', firestoreUser.workspace.id);
    
    // Add lastUpdated timestamp
    const updateData = {
      ...updates,
      lastUpdated: Date.now()
    };

    await updateDoc(workspaceRef, {
      stats: updateData
    });
  } catch (error) {
    console.error('Error updating workspace stats:', error);
    throw new Error('Failed to update workspace stats');
  }
}

export async function updateWorkspaceOnboardingStep(
  firestoreUser: User,
  onboardingStep: number
): Promise<void> {
  // Demo mode check - simulate success
  if (isDemoWorkspace(firestoreUser)) {
    // Simulate a delay to mimic real API call
    await new Promise(resolve => setTimeout(resolve, 500));
    return;
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const workspaceRef = doc(db, 'workspaces', firestoreUser.workspace.id);
    await updateDoc(workspaceRef, {
      onboardingStep: onboardingStep
    });
    console.log(`Updated workspace onboarding step to: ${onboardingStep}`);
  } catch (error) {
    console.error('Error updating workspace onboarding step:', error);
    throw new Error('Failed to update workspace onboarding step');
  }
}

// Helper functions for incrementing specific counts
export async function initializeBlockStats(
  firestoreUser: User,
  blockId: string,
  blockName: string
): Promise<void> {
  if (isDemoWorkspace(firestoreUser)) {
    await new Promise(resolve => setTimeout(resolve, 300));
    return;
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const workspaceRef = doc(db, 'workspaces', firestoreUser.workspace.id);
    
    // Initialize block stats if they don't exist
    await updateDoc(workspaceRef, {
      [`stats.blocks.${blockId}`]: {
        totalApartmentUnits: 0,
        totalParkings: 0,
        totalAmenities: 0,
        totalFloors: 0
      },
      'stats.lastUpdated': Date.now()
    });
    
    console.log(`Initialized stats for block ${blockName} (${blockId})`);
  } catch (error) {
    console.error('Error initializing block stats:', error);
    throw new Error('Failed to initialize block stats');
  }
}

export async function incrementBlockCount(
  firestoreUser: User,
  blockId: string,
  field: 'totalApartmentUnits' | 'totalParkings' | 'totalAmenities',
  amount: number = 1
): Promise<void> {
  if (isDemoWorkspace(firestoreUser)) {
    await new Promise(resolve => setTimeout(resolve, 300));
    return;
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const workspaceRef = doc(db, 'workspaces', firestoreUser.workspace.id);
    
    await updateDoc(workspaceRef, {
      [`stats.blocks.${blockId}.${field}`]: increment(amount),
      'stats.lastUpdated': Date.now()
    });
  } catch (error) {
    console.error('Error incrementing block count:', error);
    throw new Error('Failed to increment block count');
  }
}

export async function incrementGlobalCount(
  firestoreUser: User,
  field: 'totalAssets' | 'totalEmployees' | 'totalVendors' | 'totalUsers',
  amount: number = 1
): Promise<void> {
  if (isDemoWorkspace(firestoreUser)) {
    await new Promise(resolve => setTimeout(resolve, 300));
    return;
  }

  try {
    if (!firestoreUser?.workspace?.id) {
      throw new Error('No workspace ID available');
    }

    const workspaceRef = doc(db, 'workspaces', firestoreUser.workspace.id);
    
    await updateDoc(workspaceRef, {
      [`stats.${field}`]: increment(amount),
      'stats.lastUpdated': Date.now()
    });
  } catch (error) {
    console.error('Error incrementing global count:', error);
    throw new Error('Failed to increment global count');
  }
}

/**
 * Comprehensive workspace deletion function that removes all related data
 * @param workspaceId - The ID of the workspace to delete
 * @param onProgress - Optional callback for progress updates
 * @returns Promise with deletion summary
 */
export async function removeBlockStats(firestoreUser: User, blockId: string): Promise<void> {
  if (!firestoreUser?.workspace?.id) {
    throw new Error('User workspace not found');
  }

  try {
    const workspaceId = firestoreUser.workspace.id;
    const workspaceRef = doc(db, 'workspaces', workspaceId);
    
    // Remove the specific block from the stats.blocks object
    await updateDoc(workspaceRef, {
      [`stats.blocks.${blockId}`]: deleteField()
    });

    // Get the current workspace stats to update totals
    const workspaceDoc = await getDoc(workspaceRef);
    if (workspaceDoc.exists()) {
      const workspaceData = workspaceDoc.data();
      const stats = workspaceData.stats || {};
      const blocks = stats.blocks || {};
      
      // Recalculate totals after removing the block
      const remainingBlocks = Object.keys(blocks).filter(id => id !== blockId);
      const totalBlocks = remainingBlocks.length;
      
      // Calculate total apartment units from remaining blocks
      let totalApartmentUnits = 0;
      for (const remainingBlockId of remainingBlocks) {
        const blockStats = blocks[remainingBlockId];
        if (blockStats && typeof blockStats === 'object' && 'units' in blockStats) {
          totalApartmentUnits += blockStats.units || 0;
        }
      }

      // Update the totals
      await updateDoc(workspaceRef, {
        'stats.totalBlocks': totalBlocks,
        'stats.totalApartmentUnits': totalApartmentUnits,
        'stats.lastUpdated': new Date().toISOString()
      });
    }

    console.log(`Block stats removed for block ${blockId}`);
  } catch (error: any) {
    console.error('Error removing block stats:', error);
    throw new Error(`Failed to remove block stats: ${error.message}`);
  }
}

export async function recalculateWorkspaceStats(firestoreUser: User): Promise<void> {
  if (!firestoreUser?.workspace?.id) {
    throw new Error('User workspace not found');
  }

  try {
    const workspaceId = firestoreUser.workspace.id;
    
    // Get all blocks for this workspace
    const blocksQuery = query(
      collection(db, 'blocks'),
      where('workspace.id', '==', workspaceId)
    );
    const blocksSnapshot = await getDocs(blocksQuery);
    const blocks = blocksSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Get all apartment units for each block
    let totalApartmentUnits = 0;
    let totalParkings = 0;
    let totalAmenities = 0;
    const blockStats: { [blockId: string]: { totalApartmentUnits: number; totalParkings: number; totalAmenities: number; totalFloors: number } } = {};

    for (const block of blocks) {
      // Get apartment units count for this block using aggregation
      const unitsQuery = query(
        collection(db, 'apartment_units'),
        where('block.id', '==', block.id)
      );
      const unitsCountSnapshot = await getCountFromServer(unitsQuery);
      const unitsCount = unitsCountSnapshot.data().count;
      
      // Get parkings count for this block using aggregation
      const parkingsQuery = query(
        collection(db, 'parkings'),
        where('block.id', '==', block.id)
      );
      const parkingsCountSnapshot = await getCountFromServer(parkingsQuery);
      const parkingsCount = parkingsCountSnapshot.data().count;
      
      // Get amenities count for this block using aggregation
      const amenitiesQuery = query(
        collection(db, 'amenities'),
        where('block.id', '==', block.id)
      );
      const amenitiesCountSnapshot = await getCountFromServer(amenitiesQuery);
      const amenitiesCount = amenitiesCountSnapshot.data().count;
      
      
      // Calculate total floors for this block (floors are stored in the block document)
      const totalFloors = (block as any).floors ? (block as any).floors.length : 0;
      
      blockStats[block.id] = { 
        totalApartmentUnits: unitsCount,
        totalParkings: parkingsCount,
        totalAmenities: amenitiesCount,
        totalFloors: totalFloors
      };
      totalApartmentUnits += unitsCount;
      totalParkings += parkingsCount;
      totalAmenities += amenitiesCount;
    }

    // Calculate total blocks
    const totalBlocks = blocks.length;
    console.log('Recalculating workspace stats - totalBlocks:', totalBlocks, 'totalApartmentUnits:', totalApartmentUnits, 'totalParkings:', totalParkings, 'totalAmenities:', totalAmenities);

    // Get all residents count using aggregation
    const residentsQuery = query(
      collection(db, 'residents'),
      where('workspace.id', '==', workspaceId)
    );
    const residentsCountSnapshot = await getCountFromServer(residentsQuery);
    const totalResidents = residentsCountSnapshot.data().count;
    console.log('Recalculating workspace stats - totalResidents:', totalResidents);

    // Get all employees count using aggregation
    const employeesQuery = query(
      collection(db, 'employees'),
      where('workspace.id', '==', workspaceId)
    );
    const employeesCountSnapshot = await getCountFromServer(employeesQuery);
    const totalEmployees = employeesCountSnapshot.data().count;

    // Get all assets count using aggregation
    const assetsQuery = query(
      collection(db, 'assets'),
      where('workspace.id', '==', workspaceId)
    );
    const assetsCountSnapshot = await getCountFromServer(assetsQuery);
    const totalAssets = assetsCountSnapshot.data().count;

    // Get all vendors count using aggregation
    const vendorsQuery = query(
      collection(db, 'vendors'),
      where('workspace.id', '==', workspaceId)
    );
    const vendorsCountSnapshot = await getCountFromServer(vendorsQuery);
    const totalVendors = vendorsCountSnapshot.data().count;

    // Get all users count using aggregation
    const usersQuery = query(
      collection(db, 'users'),
      where('workspace.id', '==', workspaceId)
    );
    const usersCountSnapshot = await getCountFromServer(usersQuery);
    const totalUsers = usersCountSnapshot.data().count;

    // Update workspace stats
    const workspaceRef = doc(db, 'workspaces', workspaceId);
    const updateData = {
      'stats.blocks': blockStats,
      'stats.totalBlocks': totalBlocks,
      'stats.totalApartmentUnits': totalApartmentUnits,
      'stats.totalParkings': totalParkings,
      'stats.totalAmenities': totalAmenities,
      'stats.totalAssets': totalAssets,
      'stats.totalEmployees': totalEmployees,
      'stats.totalVendors': totalVendors,
      'stats.totalResidents': totalResidents,
      'stats.totalUsers': totalUsers,
      'stats.lastUpdated': Date.now()
    };
    
    console.log('Updating workspace stats with:', updateData);
    await updateDoc(workspaceRef, updateData);

    console.log('Workspace stats recalculated successfully');
  } catch (error: any) {
    console.error('Error recalculating workspace stats:', error);
    throw new Error(`Failed to recalculate workspace stats: ${error.message}`);
  }
}

/**
 * Get collection statistics for a workspace
 * @param workspaceId - The ID of the workspace
 * @returns Promise with collection counts
 */
export const getWorkspaceCollectionStats = async (workspaceId: string): Promise<{
  collections: Array<{
    name: string;
    count: number;
    displayName: string;
  }>;
  totalRecords: number;
}> => {
  const collections = [
    { name: 'apartment_units', displayName: 'Apartment Units' },
    { name: 'assets', displayName: 'Assets' },
    { name: 'attendance', displayName: 'Attendance' },
    { name: 'blocks', displayName: 'Blocks' },
    { name: 'calendar_events', displayName: 'Calendar Events' },
    { name: 'chatThreads', displayName: 'Chat Threads' },
    { name: 'customers', displayName: 'Customers' },
    { name: 'devices', displayName: 'Devices' },
    { name: 'employees', displayName: 'Employees' },
    { name: 'guidelines', displayName: 'Guidelines' },
    { name: 'incidents', displayName: 'Incidents' },
    { name: 'invitations', displayName: 'Invitations' },
    { name: 'leads', displayName: 'Leads' },
    { name: 'leave_requests', displayName: 'Leave Requests' },
    { name: 'lost_and_found', displayName: 'Lost & Found' },
    { name: 'maintenance_requests', displayName: 'Maintenance Requests' },
    { name: 'news', displayName: 'News' },
    { name: 'notifications', displayName: 'Notifications' },
    { name: 'packages', displayName: 'Packages' },
    { name: 'parkings', displayName: 'Parkings' },
    { name: 'payables', displayName: 'Payables' },
    { name: 'payrolls', displayName: 'Payrolls' },
    { name: 'price_plans', displayName: 'Price Plans' },
    { name: 'prospectTags', displayName: 'Prospect Tags' },
    { name: 'receivables', displayName: 'Receivables' },
    { name: 'reservations', displayName: 'Reservations' },
    { name: 'residents', displayName: 'Residents' },
    { name: 'support_tickets', displayName: 'Support Tickets' },
    { name: 'tasks', displayName: 'Tasks' },
    { name: 'users', displayName: 'Users' },
    { name: 'vendors', displayName: 'Vendors' },
    { name: 'visitors', displayName: 'Visitors' }
  ];

  const collectionStats = [];
  let totalRecords = 0;

  for (const collectionInfo of collections) {
    try {
      const collectionRef = collection(db, collectionInfo.name);
      const q = query(collectionRef, where('workspace.id', '==', workspaceId));
      const countSnapshot = await getCountFromServer(q);
      const count = countSnapshot.data().count;
      
      collectionStats.push({
        name: collectionInfo.name,
        count,
        displayName: collectionInfo.displayName
      });
      
      totalRecords += count;
    } catch (error) {
      console.error(`Error counting ${collectionInfo.name}:`, error);
      collectionStats.push({
        name: collectionInfo.name,
        count: 0,
        displayName: collectionInfo.displayName
      });
    }
  }

  return {
    collections: collectionStats,
    totalRecords
  };
};

export const deleteWorkspaceCompletely = async (
  workspaceId: string,
  onProgress?: (progress: number, status: string) => void
): Promise<{
  success: boolean;
  deletedCounts: Record<string, number>;
  errors: string[];
}> => {
  const deletedCounts: Record<string, number> = {};
  const errors: string[] = [];
  
  // Collections that reference workspace by workspace.id
  const workspaceCollections = [
    'apartment_units',
    'assets', 
    'attendance',
    'blocks',
    'calendar_events',
    'chatThreads',
    'customers',
    'devices',
    'employees',
    'guidelines',
    'incidents',
    'invitations',
    'leads',
    'leave_requests',
    'lost_and_found',
    'maintenance_requests',
    'news',
    'notifications',
    'packages',
    'parkings',
    'payables',
    'payrolls',
    'price_plans',
    'prospectTags',
    'receivables',
    'reservations',
    'residents',
    'support_tickets',
    'tasks',
    'users',
    'vendors',
    'visitors'
  ];

  try {
    console.log(`Starting comprehensive deletion for workspace: ${workspaceId}`);
    onProgress?.(0, 'Starting workspace deletion...');
    
    // Process each collection in batches
    for (let i = 0; i < workspaceCollections.length; i++) {
      const collectionName = workspaceCollections[i];
      const progress = Math.round((i / workspaceCollections.length) * 90); // 90% for collections, 10% for workspace
      onProgress?.(progress, `Processing ${collectionName}...`);
      try {
        console.log(`Processing collection: ${collectionName}`);
        
        // Query documents that reference this workspace
        const collectionRef = collection(db, collectionName);
        const q = query(collectionRef, where('workspace.id', '==', workspaceId));
        const snapshot = await getDocs(q);
        
        if (snapshot.empty) {
          deletedCounts[collectionName] = 0;
          continue;
        }

        // Process in batches of 500 (Firestore batch limit)
        const batchSize = 500;
        const docs = snapshot.docs;
        let batchCount = 0;
        
        for (let i = 0; i < docs.length; i += batchSize) {
          const batch = writeBatch(db);
          const batchDocs = docs.slice(i, i + batchSize);
          
          batchDocs.forEach(docSnapshot => {
            batch.delete(docSnapshot.ref);
          });
          
          await batch.commit();
          batchCount += batchDocs.length;
          console.log(`Deleted ${batchDocs.length} documents from ${collectionName} (batch ${Math.floor(i / batchSize) + 1})`);
        }
        
        deletedCounts[collectionName] = batchCount;
        console.log(`Completed ${collectionName}: ${batchCount} documents deleted`);
        
      } catch (collectionError: any) {
        const errorMsg = `Failed to delete from ${collectionName}: ${collectionError.message}`;
        console.error(errorMsg, collectionError);
        errors.push(errorMsg);
        deletedCounts[collectionName] = 0;
      }
    }

    // Finally, delete the workspace document itself
    onProgress?.(95, 'Deleting workspace document...');
    try {
      const workspaceRef = doc(db, 'workspaces', workspaceId);
      await deleteDoc(workspaceRef);
      deletedCounts['workspaces'] = 1;
      console.log('Workspace document deleted successfully');
    } catch (workspaceError: any) {
      const errorMsg = `Failed to delete workspace document: ${workspaceError.message}`;
      console.error(errorMsg, workspaceError);
      errors.push(errorMsg);
    }
    
    onProgress?.(100, 'Deletion completed!');

    const totalDeleted = Object.values(deletedCounts).reduce((sum, count) => sum + count, 0);
    console.log(`Workspace deletion completed. Total documents deleted: ${totalDeleted}`);
    console.log('Deletion summary:', deletedCounts);
    
    if (errors.length > 0) {
      console.warn('Deletion completed with errors:', errors);
    }

    return {
      success: errors.length === 0,
      deletedCounts,
      errors
    };

  } catch (error: any) {
    console.error('Critical error during workspace deletion:', error);
    return {
      success: false,
      deletedCounts,
      errors: [...errors, `Critical error: ${error.message}`]
    };
  }
}; 